import { BrandMark } from "@/components/BrandMark";

interface BrandWordmarkProps {
  /** Visual size — matches the prior logo's breakpoints. */
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  /** Show the bracket icon mark alongside the text. Default true. */
  showMark?: boolean;
}

const sizeMap: Record<NonNullable<BrandWordmarkProps["size"]>, { text: string; mark: number; gap: string }> = {
  sm: { text: "text-base",                mark: 18, gap: "gap-1.5" },
  md: { text: "text-xl sm:text-2xl",     mark: 24, gap: "gap-2"    },
  lg: { text: "text-3xl sm:text-4xl",    mark: 32, gap: "gap-2.5"  },
  xl: { text: "text-5xl sm:text-6xl",    mark: 44, gap: "gap-3"    },
};

/**
 * The full FC Bracket lockup: bracket glyph + "fc·bracket" wordmark.
 *
 * Visual logic:
 *  - Bracket mark (from <BrandMark />) on the left in --primary color
 *  - "fc·bracket" lowercase Inter 800 with a primary-colored middot
 *    separator, Nike-style tight tracking
 *
 * Both elements inherit --primary, so the mark + middot match whatever
 * theme is active (volt on the homepage, gold in the authenticated app).
 */
export function BrandWordmark({ size = "md", className = "", showMark = true }: BrandWordmarkProps) {
  const s = sizeMap[size];
  return (
    <span
      className={`inline-flex items-center ${s.gap} select-none ${className}`}
      aria-label="FC Bracket"
    >
      {showMark && <BrandMark size={s.mark} className="text-primary shrink-0" />}
      <span
        className={`font-display font-black tracking-[-0.03em] leading-none ${s.text}`}
      >
        FC
        <span className="text-primary mx-0.5" aria-hidden>·</span>
        Bracket
      </span>
    </span>
  );
}
